import { type ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { API } from '@/api'
import { Loading } from '@/components/base/Loading'
import { Pagination } from '@/components/base/Pagination'
import { EmptyView } from '@/components/EmptyView'
import { Trans } from '@/components/Trans'
import { getInstanceListExpandPath, useUnifiedContext } from '@/contexts/UnifiedContext'
import { PurchasedEIP } from '@/models/eip'
import { Instance, InstanceStatus } from '@/models/instance'
import { cn } from '@/utils/style'

export type SubTab = 'instance' | 'autoDeploy' | 'autoScaling'

export function RelatedResourceContent({
  eip,
  activeSubTab,
  onSubTabChange,
  subTabs,
}: {
  eip: PurchasedEIP
  activeSubTab: SubTab
  onSubTabChange: (tab: SubTab) => void
  subTabs: { label: ReactNode; value: SubTab }[]
}) {
  return (
    <div className="bg-neutral-50 rounded-lg p-4">
      {/* Sub-tabs with underline style */}
      <div className="flex gap-6 border-b border-neutral-200">
        {subTabs.map((tab) => (
          <button
            key={tab.value}
            className={cn(
              'pb-3 t-x-sb cursor-pointer transition-colors',
              activeSubTab === tab.value ? 'border-b-[3px] border-secondary' : 'text-tertiary',
            )}
            onClick={() => onSubTabChange(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sub-tab content */}
      <div className="mt-4">
        {activeSubTab === 'instance' ? (
          <InstanceSubTable eip={eip} />
        ) : (
          <EmptyView
            imageSize="small"
            title={
              <span className="t-sm text-secondary">
                <Trans ns="eip" i18nKey="detail.emptyResource">
                  This IP is not linked to any resources.
                </Trans>
              </span>
            }
          />
        )}
      </div>
    </div>
  )
}

function InstanceSubTable({ eip }: { eip: PurchasedEIP }) {
  const ctx = useUnifiedContext()
  const [loading, setLoading] = useState(false)
  const [instances, setInstances] = useState<Instance[]>([])
  const [page, setPage] = useState(1)
  const pageSize = 5

  useEffect(() => {
    let active = true
    const getInstances = async () => {
      setLoading(true)
      try {
        const res = await API.getNodeList({
          page: 1,
          per_page: 500,
          statuses: String(InstanceStatus.Running),
          region_filter_criteria: 0,
          regions: [eip.domain],
        })
        if (res.code === 0 && res.userNodes && active) {
          const filtered = res.userNodes.filter((node) =>
            node.nats?.some((n) => n.net.netResID === eip.resID),
          )
          setInstances(filtered.map((info) => new Instance(info)))
        }
      } catch (e) {
        console.error(e)
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    getInstances()

    return () => {
      active = false
    }
  }, [eip.resID, eip.domain])

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center p-8">
        <Loading />
      </div>
    )
  }

  if (instances.length === 0) {
    return (
      <EmptyView
        imageSize="small"
        title={
          <span className="t-sm text-secondary">
            <Trans ns="eip" i18nKey="detail.emptyResource">
              This IP is not linked to any resources.
            </Trans>
          </span>
        }
      />
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0">
        <thead>
          <tr className="[&>th]:border-b [&>th]:border-neutral-100">
            <th className="text-left t-x-sb text-tertiary px-4 py-3">
              <Trans ns="eip" i18nKey="detail.thInstanceId">
                Instance ID
              </Trans>
            </th>
            <th className="text-left t-x-sb text-tertiary px-4 py-3">
              <Trans ns="eip" i18nKey="detail.thName">
                Name
              </Trans>
            </th>
            <th className="text-left t-x-sb text-tertiary px-4 py-3">
              <Trans ns="eip" i18nKey="detail.thPort">
                Port
              </Trans>
            </th>
            <th className="text-left t-x-sb text-tertiary px-4 py-3">
              <Trans ns="eip" i18nKey="detail.thInstanceStatus">
                Instance Status
              </Trans>
            </th>
            <th className="text-left t-x-sb text-tertiary px-4 py-3">
              <Trans ns="eip" i18nKey="detail.thAction">
                Action
              </Trans>
            </th>
          </tr>
        </thead>
        <tbody className="bg-panel-white [&>tr:first-child>td:first-child]:rounded-tl-lg [&>tr:first-child>td:last-child]:rounded-tr-lg [&>tr:last-child>td:first-child]:rounded-bl-lg [&>tr:last-child>td:last-child]:rounded-br-lg">
          {instances.slice((page - 1) * pageSize, page * pageSize).map((row) => {
            const ports =
              row.nats?.find((n) => n.net.netResID === eip.resID)?.portMappings.items || []
            const portsDisplay = ports.map((p) => p.listenPort).join(', ') || '-'
            const instanceDetailUrl = getInstanceListExpandPath(ctx, row.resID)

            return (
              <tr
                key={row.resID}
                className="[&>td]:border-b [&>td]:border-neutral-100 last:border-b-0"
              >
                <td className="t-x-sb py-3 pr-4">
                  <Link to={instanceDetailUrl} className="text-highlight">
                    {row.resID}
                  </Link>
                </td>
                <td className="t-x-sb text-secondary py-3 pr-4">{row.displayTitle || '-'}</td>
                <td className="t-x-sb text-secondary py-3 pr-4">{portsDisplay}</td>
                <td className="t-x-sb py-3 pr-4">
                  <span className={cn(row.isRunning ? 'text-active' : 'text-tertiary')}>
                    {row.statusText}
                  </span>
                </td>
                <td className="t-x-sb py-3">
                  <Link to={instanceDetailUrl} className="text-highlight t-x-sb">
                    <Trans ns="eip" i18nKey="detail.actionDetail">
                      Detail
                    </Trans>
                  </Link>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {instances.length > 0 && (
        <Pagination
          size={pageSize}
          total={instances.length}
          current={page}
          className="justify-end mt-2"
          activeClassName="bg-transparent"
          navClassName="bg-transparent"
          onChange={setPage}
        />
      )}
    </div>
  )
}
